import {graphql, useFragment} from 'react-relay'
import type {LinkedPullRequest$data, LinkedPullRequest$key} from './__generated__/LinkedPullRequest.graphql'
import {ActionList, Box, Link, Octicon} from '@primer/react'
import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {PullRequestStateIcons} from '@github-ui/timeline-items/Icons'
import {Tooltip} from '@primer/react/experimental'

const LinkedPullRequestPull = graphql`
  fragment LinkedPullRequest on PullRequest {
    url
    number
    state
    repository {
      name
      nameWithOwner
      owner {
        login
      }
    }
  }
`

export const LinkedPullRequest = ({
  pullKey,
  issueRepositoryName,
  isListItem,
  itemKey,
  isSticky,
}: {
  pullKey: LinkedPullRequest$key
  issueRepositoryName: string
  isListItem?: boolean
  itemKey?: string
  isSticky?: boolean
}) => {
  const pull = useFragment(LinkedPullRequestPull, pullKey)
  const owner = pull.repository?.owner?.login
  const repoName = pull.repository?.name

  const isExternal = pull.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()
  const {icon, color} = getPrIconColor(pull)

  // This not straight-forward logic is to make sure that we are always showing the pull number and only truncating
  // the org/repo prefix
  const title = isExternal ? (
    <Box sx={{display: 'flex', gap: '0'}}>
      <Box
        sx={{
          maxWidth: 100,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >{`${owner}/${repoName}`}</Box>
      <span>#{pull.number}</span>
    </Box>
  ) : (
    <>{`#${pull.number}`}</>
  )

  const tooltipTitle = `${owner}/${repoName}#${pull.number}`

  const hoverCard = isSticky
    ? null
    : issueLinkedPullRequestHovercardPath({
        owner: pull.repository.owner.login,
        repo: pull.repository.name,
        pullRequestNumber: pull.number,
      })

  if (isListItem) {
    return (
      <ActionList.LinkItem href={pull.url} key={itemKey} target="_blank">
        <ActionList.LeadingVisual>
          <Octicon icon={icon} sx={{color}} />
        </ActionList.LeadingVisual>
        {title}
      </ActionList.LinkItem>
    )
  }

  const linkElement = (
    <Link
      sx={{
        fontSize: 0,
        overflow: isExternal ? 'hidden' : 'visible',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
      }}
      href={pull.url}
      data-hovercard-url={hoverCard}
      target="_blank"
    >
      {title}
    </Link>
  )

  // Not showing a tooltip for PRs in the same repo, as they can't be truncated so we know all of the information is already visible
  return isExternal ? <Tooltip text={tooltipTitle}>{linkElement}</Tooltip> : linkElement
}

const getPrIconColor = (pullRequest: LinkedPullRequest$data) => {
  const state = pullRequest.state === 'MERGED' ? 'MERGED' : 'OPEN'
  const {color, icon} = PullRequestStateIcons[state]!
  return {color, icon}
}

try{ LinkedPullRequest.displayName ||= 'LinkedPullRequest' } catch {}